import * as React from "react";
import { ConnectProto, RouteStatus } from "@racoon/api";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { getEnumName, JOB_STATUS_TONE, ROUTE_STATUS_TONE, type StatusTone } from "@racoon/lib";
import { cn, StatusPill } from "@racoon/ui";
import { getStatusDisplay } from "../RouteStatusBadge";

/** `STATUS_TONE_CLASS` in the current design tokens (Figma Status/<stage>/idle). */
const TONE_DS: Record<StatusTone, string> = {
  pending: "bg-ds-status-created-idle text-ds-text-on-action",
  assigned: "bg-ds-status-assigned-idle text-ds-text-information",
  active: "bg-ds-status-in-progress-idle text-ds-text-on-action",
  done: "bg-ds-status-completed-idle text-ds-text-on-action",
  cancelled: "bg-ds-status-cancelled-idle text-ds-text-on-action",
  failed: "bg-ds-status-failed-idle text-ds-text-on-action",
};

/** Figma "Job Status": a fixed-width pill, so a column of them lines up. */
export const JobStatusPill: React.FC<{ status: ConnectProto.JobStatus; className?: string }> = ({
  status,
  className,
}) => {
  const t = useT();
  const name = getEnumName(ConnectProto.JobStatusSchema, status) ?? "";
  return (
    <StatusPill tone={cn("w-[118px]", TONE_DS[JOB_STATUS_TONE[name] ?? "assigned"], className)}>
      {t(Namespace.Enum, name as TranslationKey)}
    </StatusPill>
  );
};

/**
 * Figma "Route Status": badge and counter colours per stage. Not approved reads
 * dark on amber, unlike the job pill, so the route keeps its own badge classes.
 */
const ROUTE_TONE_DS: Record<StatusTone, { badge: string; counter: string }> = {
  pending: {
    badge: "bg-ds-status-created-idle text-ds-text-body",
    counter: "bg-ds-surface-attention-subtle text-ds-text-body",
  },
  assigned: { badge: TONE_DS.assigned, counter: "bg-ds-surface-action text-ds-text-on-action" },
  active: { badge: TONE_DS.active, counter: "bg-ds-surface-primary text-ds-text-information" },
  done: { badge: TONE_DS.done, counter: "bg-ds-surface-primary text-ds-text-success" },
  cancelled: { badge: TONE_DS.cancelled, counter: "bg-ds-surface-primary text-ds-text-body" },
  // No route stage maps here; kept for the Record.
  failed: { badge: TONE_DS.failed, counter: "bg-ds-surface-primary text-ds-text-body" },
};

/**
 * Figma "Route Status": the route's status with a jobs counter — done / total
 * while the route runs, the total otherwise.
 */
export const RouteStatusPill: React.FC<{ route: ConnectProto.Route }> = ({ route }) => {
  const t = useT();
  const name = getEnumName(ConnectProto.RouteStatusSchema, route.status) ?? "";
  const tone = ROUTE_STATUS_TONE[name] ?? "assigned";
  const { badge, counter } = ROUTE_TONE_DS[tone];
  const jobs = route.jobs ?? [];
  const done = jobs.filter(
    (job) =>
      JOB_STATUS_TONE[getEnumName(ConnectProto.JobStatusSchema, job.status) ?? ""] === "done",
  ).length;
  return (
    <span
      className={cn(
        // One width for every stage, the counter on the right edge, so trips line up.
        "inline-flex h-6 w-[136px] shrink-0 items-center justify-between gap-1 whitespace-nowrap rounded-2xl py-1 pl-2 pr-1 text-xs font-medium leading-4",
        badge,
      )}
    >
      {getStatusDisplay(t, name as RouteStatus).text}
      {jobs.length > 0 && (
        <span
          className={cn(
            "flex h-4 items-center rounded-full px-1 text-[10px] font-semibold leading-[10px]",
            counter,
          )}
        >
          {tone === "active" ? `${done}/${jobs.length}` : jobs.length}
        </span>
      )}
    </span>
  );
};
